import React, { useEffect, useState } from "react";
import { LineChart as RLineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Plus, X, FileText, Scale, HeartPulse, Droplets, Footprints, Activity, Percent } from "lucide-react";
import { toast } from "sonner";
import { api, palette } from "@/lib/api";
import { Card } from "@/pages/bitepal/Home";

const METRICS = {
  weight: { label: "Weight", unit: "lb", icon: Scale, color: palette.emerald },
  body_fat: { label: "Body Fat", unit: "%", icon: Percent, color: palette.violet },
  blood_pressure: { label: "Blood Pressure", unit: "mmHg", icon: HeartPulse, color: palette.red, secondary: true },
  blood_glucose: { label: "Glucose", unit: "mg/dL", icon: Droplets, color: palette.orange },
  steps: { label: "Steps", unit: "", icon: Footprints, color: palette.blue },
  resting_hr: { label: "Resting HR", unit: "bpm", icon: Activity, color: palette.cyan },
};

export default function Vitals() {
  const [latest, setLatest] = useState({});
  const [active, setActive] = useState("weight");
  const [trend, setTrend] = useState(null);
  const [logM, setLogM] = useState(null);
  const [val, setVal] = useState(""); const [val2, setVal2] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = async (metric) => {
    try { const [l, t] = await Promise.all([api("/vitals/latest"), api(`/vitals/trends?metric=${metric}&days=30`)]); setLatest(l); setTrend(t); } catch {}
  };
  useEffect(() => { load(active); }, [active]);

  const save = async () => { if (!val) { toast.error("Enter a value"); return; }
    try { await api("/vitals", { method: "POST", body: { metric: logM, value: parseFloat(val), secondary: val2 ? parseFloat(val2) : null } }); setLogM(null); setVal(""); setVal2(""); load(active); }
    catch (e) { toast.error(e.message); } };

  const exportPdf = async () => { setExporting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/report/clinical`, { credentials: "include" });
      if (!res.ok) throw new Error("Could not generate report");
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "STILL_Clinical_Summary.pdf"; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setExporting(false); } };

  const meta = METRICS[active];
  return (
    <div>
      <h1 className="text-3xl font-extrabold mb-5">Vitals</h1>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {Object.entries(METRICS).map(([k, m]) => { const cur = latest[k]; const Icon = m.icon; const isA = active === k;
          return (
            <button key={k} onClick={() => setActive(k)} className={`bg-white border rounded-2xl p-3 text-left ${isA ? "" : "border-[#E3D9C8]"}`} style={isA ? { borderColor: m.color } : {}}>
              <Icon size={18} style={{ color: m.color }} />
              <p className="text-[11px] text-slate-500 font-semibold mt-1.5">{m.label}</p>
              <p className="text-base font-extrabold">{cur ? `${cur.value}${cur.secondary ? "/" + cur.secondary : ""}` : "—"} <span className="text-[10px] text-slate-500">{m.unit}</span></p>
            </button>
          ); })}
      </div>

      <Card className="mt-4">
        <div className="flex justify-between items-center"><h3 className="font-bold">{meta.label} · 30 days</h3>
          <button data-testid="vitals-log-button" onClick={() => setLogM(active)} className="flex items-center gap-1 bg-[#651F33] text-[#F5F0E8] px-3 py-1.5 rounded-full font-bold text-sm"><Plus size={16} /> Log</button></div>
        <div className="mt-4 h-56">
          {trend?.series?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <RLineChart data={trend.series}>
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10 }} domain={["auto", "auto"]} width={30} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #E3D9C8", borderRadius: 12, color: "#0f172a" }} />
                <Line type="monotone" dataKey="value" stroke={meta.color} strokeWidth={2.5} dot={{ r: 3, fill: meta.color }} />
              </RLineChart>
            </ResponsiveContainer>
          ) : <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data yet — log a reading to see your trend.</div>}
        </div>
      </Card>

      <button data-testid="vitals-export-button" onClick={exportPdf} disabled={exporting} className="w-full mt-4 py-3.5 rounded-full bg-white border border-[#E3D9C8] font-bold flex items-center justify-center gap-2"><FileText size={18} /> {exporting ? "Preparing…" : "Export Clinical Summary (PDF)"}</button>

      {logM && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-end md:items-center justify-center p-4" onClick={() => setLogM(null)}>
          <div className="bg-[#ECE4D6] border border-[#E3D9C8] rounded-3xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-extrabold">Log {METRICS[logM].label}</h2><button onClick={() => setLogM(null)}><X className="text-slate-500" /></button></div>
            <input data-testid="vitals-value-input" autoFocus className="w-full bg-white border border-[#E3D9C8] rounded-2xl px-4 py-3 text-slate-900 outline-none focus:border-[#651F33]" value={val} onChange={(e) => setVal(e.target.value)} inputMode="numeric" placeholder={METRICS[logM].secondary ? "Systolic" : `Value (${METRICS[logM].unit})`} />
            {METRICS[logM].secondary && <input className="w-full mt-2 bg-white border border-[#E3D9C8] rounded-2xl px-4 py-3 text-slate-900 outline-none focus:border-[#651F33]" value={val2} onChange={(e) => setVal2(e.target.value)} inputMode="numeric" placeholder="Diastolic" />}
            <button data-testid="vitals-save-button" onClick={save} className="w-full mt-4 py-3.5 rounded-full bg-[#651F33] text-[#F5F0E8] font-bold">Save reading</button>
          </div>
        </div>
      )}
    </div>
  );
}
